import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import FormButton from 'components/form-components/FormButton';
import { newScheduleTabList } from '../../utils';
import BasicTab from './BasicTab/BasicTab';
import { CallAPI } from 'actions/General';
import { geoCoordinatesRegex } from 'pages/datasource/utils';
import toast from 'react-hot-toast';
import AdvanceTab from './AdvanceTab/AdvanceTab';
import ConfirmationModal from 'components/common-components/ConfirmationModal';
import { useTranslation } from 'react-i18next';
import TabHeaderNew from 'components/common-components/TabHeaderNew';
import FormLoadingButton from 'components/form-components/FormLoadingButton';

const ScheduleEditForm = ({ handleClose, refreshList, editMode, scheduleData }) => {
  const { t } = useTranslation();

  const [selectedTab, setSelectedTab] = useState(newScheduleTabList[0]);
  const [values, setValues] = useState({
    title: '',
    description: '',
    form: null,
    companyField: null,
    companyFieldValue: null,
    companyFieldRowId: null,
    autoFillCompanyField: false,
    assignedUsers: [],
    assignedGroups: [],
    priority: 'low',
    labels: [],
    location: '',
    scheduleType: 'one_time',
    startDate: '',
    endDate: '',
    isScheduled: true,

    recurringFrequency: '',
    recurringInterval: null,
    recurringStartsAt: '',
    recurringEndsAt: '',
    recurringStartTime: '',

    daysOfWeek: [],
    repeatsOn: [],
    gracePeriodTime: 1,
    gracePeriodFormat: 'minutes',

    allowOverDue: false,
    reviewPeriodTime: 60,
    reviewPeriodFormat: 'minutes',
  });

  const [formsOptions, setFormsOptions] = useState([]);
  const [labelOptions, setLabelOptions] = useState([]);
  const [formCompanyFieldOptionsMap, setFormCompanyFieldOptionsMap] = useState({});
  const [formCompanyFieldValueOptionsMap, setFormCompanyFieldValueOptionsMap] = useState({});
  const [displayValueLookup, setDisplayValueLookup] = useState({});
  const [openUpdateConfirm, setOpenUpdateConfirm] = useState(false);
  const [loadingAction, setLoadingAction] = useState(null);

  const mapExistingScheduleToFormValues = (data) => {
    return {
      referenceNumber: data.referenceNumber,
      title: data.title || '',
      description: data.description || '',
      form: data.form?._id || null,
      companyField: data.companyField?._id || null,
      companyFieldValue: data.companyFieldValue || null,
      companyFieldRowId: data.companyFieldRowId || null,
      autoFillCompanyField: data.autoFillCompanyField ?? false,
      assignedUsers: data.assignedUsers || [],
      assignedGroups: data.assignedGroups || [],
      priority: data.priority || 'low',
      labels: (data.labels || []).map((l) => l._id),
      location: data.location || '',
      scheduleType: data.scheduleType || 'one_time',
      isScheduled: data.scheduleType !== 'not_scheduled',

      // one time
      startDate: data.scheduleType === 'one_time' ? data.startDate : '',
      endDate: data.scheduleType === 'one_time' ? data.endDate : '',

      // recurring
      recurringFrequency: data.recurringDetails?.frequency || '',
      recurringInterval: data.recurringDetails?.interval || null,
      recurringStartsAt:
        data.scheduleType === 'recurring'
          ? new Date(new Date(data.startDate).setHours(0, 0, 0, 0)).toISOString()
          : '',
      recurringEndsAt: data.scheduleType === 'recurring' ? data.endDate : '',
      recurringStartTime: data.recurringDetails?.startTime || '',
      daysOfWeek: data.recurringDetails?.daysOfWeek || [],
      repeatsOn: data.recurringDetails?.repeatsOn || [],
      gracePeriodTime: data.recurringDetails?.gracePeriodTime,
      gracePeriodFormat: data.recurringDetails?.gracePeriodFormat,

      allowOverDue: data.allowOverDue || false,
      reviewPeriodTime: data.reviewPeriodTime,
      reviewPeriodFormat: data.reviewPeriodFormat,
    };
  };

  useEffect(() => {
    if (editMode && scheduleData) {
      setValues(mapExistingScheduleToFormValues(scheduleData));
    }
  }, [editMode, scheduleData]);

  const isValid = () => {
    if (!values.title) {
      toast.error('Title is required');
      return false;
    }

    if (!values.form) {
      toast.error('Form is required');
      return false;
    }

    if (values.companyField && !values.companyFieldRowId) {
      toast.error('Please select company field value');
      return false;
    }

    if (values.isScheduled) {
      if (values.scheduleType === 'one_time') {
        if (!values.startDate) {
          toast.error('Please select starts time');
          return false;
        }
        if (!values.endDate) {
          toast.error('Please select ends time');
          return false;
        }
      } else if (values.scheduleType === 'recurring') {
        if (!values.recurringStartsAt) {
          toast.error('Please select starts time');
          return false;
        }
        if (!values.recurringFrequency) {
          toast.error('Please select recurrring frequency');
          return false;
        }
        if (values.recurringFrequency === 'weekly' && !values.daysOfWeek.length) {
          toast.error('Please select days');
          return false;
        }
        if (values.recurringFrequency === 'monthly' && !values.repeatsOn.length) {
          toast.error('Please select repeats on value');
          return false;
        }

        if (!values.recurringInterval) {
          toast.error('Please select recurrring interval');
          return false;
        }
        if (!values.recurringStartTime) {
          toast.error('Please select recurrring start time');
          return false;
        }
        if (!values.recurringEndsAt) {
          toast.error('Please select ends time');
          return false;
        }
        if (values.gracePeriodTime <= 0) {
          toast.error('Please add grace period');
          return false;
        }
      }
    }

    if (values.location && !geoCoordinatesRegex.test(values.location)) {
      toast.error('Invalid location field format');
      return false;
    }

    return true;
  };

  const submitSchedule = async (endpoint, extraPayload = {}) => {
    if (!isValid()) return;

    const scheduleType = values.isScheduled ? values.scheduleType : 'not_scheduled';

    let startDate = '';
    let endDate = '';

    if (scheduleType === 'recurring') {
      if (values.recurringStartsAt && values.recurringStartTime) {
        startDate = mergeDateAndTimeISO(values.recurringStartsAt, values.recurringStartTime);
      }
      if (values.recurringEndsAt) {
        endDate = values.recurringEndsAt;
      }
    }

    const finalStartDate =
      scheduleType === 'one_time'
        ? values.startDate
        : scheduleType === 'recurring'
        ? startDate
        : null;

    const finalEndDate =
      scheduleType === 'one_time' ? values.endDate : scheduleType === 'recurring' ? endDate : null;

    if (values.scheduleType !== 'not_scheduled' && finalStartDate && finalEndDate) {
      const localStartDate = new Date(finalStartDate);
      const localEndDate = new Date(finalEndDate);
      if (!scheduleData || scheduleData?.status === 'draft') {
        const now = new Date();
        if (localStartDate <= now) {
          toast.error('Start time cannot be earlier than the current time');
          return;
        }
      } else {
        if (localStartDate < new Date(scheduleData.startDate)) {
          toast.error(
            'Start date should be later than or equal to the previous start date and time',
          );
          return;
        }
      }
      if (localStartDate >= localEndDate) {
        toast.error('End date must be later than the start date');
        return false;
      }
    }

    const scheduleAssignees = canChangeAssignees()
      ? { assignedUsers: values.assignedUsers, assignedGroups: values.assignedGroups }
      : {};
    const payload = {
      title: values.title,
      description: values.description,
      form: values.form,
      companyField: values.companyField,
      companyFieldValue: values.companyFieldValue,
      companyFieldRowId: values.companyFieldRowId,
      autoFillCompanyField: values.autoFillCompanyField,
      ...scheduleAssignees,
      priority: values.priority,
      scheduleType: scheduleType,
      startDate: finalStartDate,
      endDate: finalEndDate,
      recurringDetails:
        scheduleType === 'recurring'
          ? {
              frequency: values.recurringFrequency,
              interval: values.recurringInterval,
              startTime: values.recurringStartTime,
              daysOfWeek: values.daysOfWeek,
              repeatsOn: values.repeatsOn,
              gracePeriodTime: values.gracePeriodTime,
              gracePeriodFormat: values.gracePeriodFormat,
            }
          : null,

      allowOverDue: scheduleType !== 'not_scheduled' ? values.allowOverDue : false,
      reviewPeriodTime: scheduleType !== 'not_scheduled' ? values.reviewPeriodTime : 0,
      reviewPeriodFormat: values.reviewPeriodFormat,

      labels: values.labels,
      location: values.location,
      ...extraPayload,
    };

    setLoadingAction(endpoint);
    const result = await CallAPI(
      endpoint,
      payload,
      editMode === 'edit' && scheduleData?._id ? scheduleData._id : null,
    );
    if (result.status) {
      const toastMessage = {
        CREATE_SCHEDULE: 'alert_success_schedule_created',
        PUBLISH_SCHEDULE: 'alert_success_schedule_published',
        UPDATE_SCHEDULE: 'alert_success_schedule_updated',
        CREATE_DRAFT_SCHEDULE: 'alert_success_schedule_draft_created',
        UPDATE_DRAFT_SCHEDULE: 'alert_success_schedule_draft_updated',
      }[endpoint];
      toast.success(t(toastMessage));
      refreshList();
      handleClose();
    } else toast.error(result.error);
    setLoadingAction(null);
  };

  const handleSave = () => {
    submitSchedule('CREATE_SCHEDULE', {});
  };

  const handleUpdate = (allowToUpdateCustomizedTasks) => {
    submitSchedule('UPDATE_SCHEDULE', { allowToUpdateCustomizedTasks });
  };

  const handlePublish = () => {
    submitSchedule('PUBLISH_SCHEDULE');
  };

  const handleDraftSave = () => {
    const endpoint = editMode === 'edit' ? 'UPDATE_DRAFT_SCHEDULE' : 'CREATE_DRAFT_SCHEDULE';
    submitSchedule(endpoint);
  };

  const canChangeAssignees = () => !(editMode === 'edit' && scheduleData.status === 'published');

  const loadScheduleDropdownOptions = async () => {
    const result = await CallAPI('GET_SCHEDULE_OPTIONS_LIST');
    if (result.status) {
      const newFormsOptions = [];
      const newFormCompanyFieldOptionsMap = {};
      const newFormCompanyFieldValueOptionsMap = {};
      const newLabelOptions = [];
      const newDisplayValueLookup = {};

      result.data.formsList.forEach((form) => {
        newDisplayValueLookup[form._id] = form.title;
        newFormsOptions.push({
          value: form._id,
          displayValue: form.title,
        });

        newFormCompanyFieldOptionsMap[form._id] = form.companyFields.map((field) => {
          newDisplayValueLookup[field._id] = field.fieldTitle;

          newFormCompanyFieldValueOptionsMap[field._id] = field.values.map((v) => {
            newDisplayValueLookup[field._id + '_' + v.rowId] = v.value;
            return { value: v.rowId, displayValue: v.value };
          });

          return {
            value: field._id,
            displayValue: field.fieldTitle,
          };
        });
      });

      result.data.labelsList.forEach((l) => {
        newLabelOptions.push({
          value: l._id,
          displayValue: l.name,
        });
      });

      setFormsOptions(newFormsOptions);
      setFormCompanyFieldOptionsMap(newFormCompanyFieldOptionsMap);
      setFormCompanyFieldValueOptionsMap(newFormCompanyFieldValueOptionsMap);
      setLabelOptions(newLabelOptions);
      setDisplayValueLookup(newDisplayValueLookup);
    }
  };

  useEffect(() => {
    loadScheduleDropdownOptions();
  }, []);

  return (
    <>
      <TabHeaderNew
        tabList={newScheduleTabList}
        selectedTab={selectedTab}
        setSelectedTab={setSelectedTab}
        classes='width-max-content p-3 d-flex gap-2 theme-font-inter-medium theme-text-black-1'
        tabClasses='px-3 theme-text-green-1'
        selectedColor='theme-text-green-1'
      />
      <div className='d-flex flex-column gap-3 p-3'>
        {selectedTab.value === 'tabs_advance' ? (
          <AdvanceTab values={values} setValues={setValues} labelOptions={labelOptions} />
        ) : (
          <BasicTab
            values={values}
            setValues={setValues}
            formsOptions={formsOptions}
            formCompanyFieldOptionsMap={formCompanyFieldOptionsMap}
            formCompanyFieldValueOptionsMap={formCompanyFieldValueOptionsMap}
            displayValueLookup={displayValueLookup}
            isEdit={!!editMode}
            allowChangeAssignee={canChangeAssignees()}
          />
        )}
      </div>

      <div className='d-flex justify-content-between px-4 py-3 border-top-grey-10-h-1'>
        <FormButton text={'button_discard'} variant='white-1' onClick={handleClose} />
        <div className='d-flex gap-2'>
          {(!editMode || editMode === 'duplicate' || scheduleData.status === 'draft') && (
            <FormLoadingButton
              text={'button_save_as_draft'}
              variant='white-1'
              onClick={handleDraftSave}
              loading={
                loadingAction === 'CREATE_DRAFT_SCHEDULE' ||
                loadingAction === 'UPDATE_DRAFT_SCHEDULE'
              }
              disabled={!!loadingAction}
            />
          )}
          {editMode && scheduleData?.status === 'draft' && (
            <FormLoadingButton
              text={'button_save'}
              variant='green-1'
              onClick={handlePublish}
              loading={loadingAction === 'PUBLISH_SCHEDULE'}
              disabled={!!loadingAction}
            />
          )}
          {(!editMode || scheduleData.status === 'published') && (
            <FormLoadingButton
              text={editMode === 'edit' ? 'button_save' : 'button_create'}
              variant='green-1'
              onClick={editMode === 'edit' ? () => setOpenUpdateConfirm(true) : handleSave}
              loading={loadingAction === 'CREATE_SCHEDULE' || loadingAction === 'UPDATE_SCHEDULE'}
              disabled={!!loadingAction}
            />
          )}
        </div>
      </div>
      {openUpdateConfirm && (
        <ConfirmationModal
          show={!!openUpdateConfirm}
          title={t('alert_schedule_edit_title')}
          message={t('alert_schedule_edit_message')}
          fullWidth
          actionList={[
            {
              color: 'black-1',
              text: t('button_cancel'),
              onClick: () => setOpenUpdateConfirm(false),
            },
            {
              color: 'blue-1',
              text: t('button_keep_manual_edits'),
              onClick: () => {
                handleUpdate(false);
                setOpenUpdateConfirm(false);
              },
            },
            {
              color: 'red-1',
              text: t('button_override_all'),
              onClick: () => {
                handleUpdate(true);
                setOpenUpdateConfirm(false);
              },
            },
          ]}
        />
      )}
    </>
  );
};

ScheduleEditForm.propTypes = {
  handleClose: PropTypes.func.isRequired,
  refreshList: PropTypes.func.isRequired,
  editMode: PropTypes.string.isRequired,
  scheduleData: PropTypes.object,
};

export default ScheduleEditForm;

function mergeDateAndTimeISO(dateISO, timeISO) {
  const baseDate = new Date(dateISO);
  const timeDate = new Date(timeISO);
  // Get LOCAL time (not UTC)
  const hours = timeDate.getHours();
  const minutes = timeDate.getMinutes();
  const seconds = timeDate.getSeconds();
  const ms = timeDate.getMilliseconds();
  // Convert local time to duration (ms)
  const durationMs = hours * 3600000 + minutes * 60000 + seconds * 1000 + ms;
  // Add to base date
  const result = new Date(baseDate.getTime() + durationMs);

  return result.toISOString();
}
